import { Component, QueryList, ViewChildren } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { DataService } from './core/services/data/data.service';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { ApiService } from './core/services/api/api.service';
import { IdbService } from './core/services/idb/idb.service';
import { OfflineService } from './core/services/offline/offline.service';
import { RoleService } from './core/services/role/role.service';
import { SyncService } from './core/services/sync/sync.service';
import { mergeMap, of, concat, switchMap, tap, concatMap, take } from 'rxjs';
import { LocalStorageService } from './core/services/local-storage/local-storage.service';
import { User } from './core/interfaces/auth/user';
import { ConfigurationService } from './core/services/configuration/configuration.service';
import { PwaBackupService } from './core/services/pwa-backup/pwa-backup.service';
import { SyncDialogComponent } from './features/sync/components/sync-dialog/sync-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from './core/services/dialog/dialog.service';
import { FeatureFlagService } from './core/services/feature-flags/feature-flag.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  @ViewChildren('sidenav') sidenavRef: QueryList<MatSidenav>;

  title: string = 'Accutrax';

  public sidenav: MatSidenav;
  public activePath: string;

  isAuthenticated: boolean = true;

  userId: string;

  constructor(
    public router: Router,
    public media: MediaObserver,
    private dataService: DataService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private idbService: IdbService,
    private roleService: RoleService,
    private syncService: SyncService,
    private offline: OfflineService,
    private localStorage: LocalStorageService,
    private configuration: ConfigurationService,
    private pwaBackUp: PwaBackupService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private featureFlags: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.authService.currentAuthStatus.subscribe((authStatus) => {
      if (authStatus) {
        this.isAuthenticated = true;
        setTimeout(() => {
          if (this.isAuthenticated && authStatus) {
            this.userId = authStatus.uid;
            this.handleInitUser().subscribe({
              next: (user) => {
                if (user) {
                  this.featureFlags.loadForCurrentCompany().subscribe();
                  const offlineRoles = ['Estimator', 'Foreman', 'Service Tech'];
                  if (offlineRoles.includes(user.userRoleName)) {
                    this.idbService
                      .getSyncStatus()
                      .pipe(take(1))
                      .subscribe((syncStatus) => {
                        if (
                          !syncStatus ||
                          syncStatus.syncStatus === 'Not Synced'
                        ) {
                          this.initialSync(user);
                        }
                      });
                  } else if (
                    user.userRoleName === 'Inside Sales' ||
                    user.userRoleName === 'Inside Sales Manager' ||
                    user.userRoleName === 'Admin' ||
                    user.userRoleName === 'Super Admin'
                  ) {
                    this.syncService
                      .clearInisdeSalesStores()
                      .pipe(
                        concatMap(() =>
                          this.syncService.insideSalesSyncOnStartUp()
                        )
                      )
                      .subscribe({
                        error: (e) => {
                          console.error(e);
                        },
                      });
                  }
                  this.configuration.getVariableConfiguration();
                  // Check for an update every hour
                  // Need to update this number in the service too if we change it here
                  this.configuration.startBackgroundTask(1000 * 60 * 60);
                  if (offlineRoles.includes(user.userRoleName)) {
                    // Keeping the delete to clear any old logs
                    this.pwaBackUp.deleteOldPWALogs();
                    // Disabling the pwa backup for now
                    // this.pwaBackUp.startBackgroundTask(1000 * 60 * 60 * 4);
                  }
                }
              },
            });
          }
        }, 1000);
      } else {
        this.isAuthenticated = false;
      }
    });

    this.sidenav = this.sidenavRef?.first;

    if (this.media.isActive('xs') && this.sidenav) {
      this.sidenav.opened = false;
    } else if (this.sidenav) {
      this.sidenav.opened = true;
    }
  }

  ngAfterViewInit() {
    // Get the sidenav reference
    if (this.sidenav) {
      this.sidenav.opened = false;
    }
  }

  handleInitUser() {
    return this.idbService.getCurrentUser().pipe(
      switchMap((user) => {
        if (user.length === 0) {
          return this.dataService.initUser().pipe(
            switchMap((uid) => this.idbService.getUser(uid)),
            tap((user) => {
              this.dataService.saveUserData(user!);
              this.roleService.updateUserRole();
            })
          );
        } else {
          return of(user[0]);
        }
      })
    );
  }

  initialSync(user: User) {
    let roleName: string = user.userRoleName;
    // Use the sync dialog with initial sync status

    if (!this.dialogService.isSyncDialogOpen()) {
      this.dialogService.markSyncDialogOpen();

      const dialogRef = this.dialog.open(SyncDialogComponent, {
        width: '50%',
        data: { userRole: roleName, initialSync: true },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.dialogService.markSyncDialogClosed();
        this.syncService.reloadSyncStatus();
      });
    }
  }

  // syncDataByRole(userRole: string, alternateRole: string) {
  //   if (userRole === 'Estimator') {
  //     return this.syncService.salesSyncOnStartUp();
  //   } else if (userRole === 'Foreman') {
  //     if (alternateRole === 'Service Tech') {
  //       return this.syncService.serviceSyncOnStartUp();
  //     } else {
  //       return this.syncService.prodSyncOnStartUp();
  //     }
  //   } else {
  //     if (alternateRole === 'Foreman') {
  //       return this.syncService.prodSyncOnStartUp();
  //     } else {
  //       return this.syncService.serviceSyncOnStartUp();
  //     }
  //   }
  // }

  getPath(path: string) {
    this.activePath = path;
  }
}
